import { useMemo, useState } from 'react';
import { X, Loader2, ClipboardList, Check, Copy, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    useGetRequestableFieldsQuery,
    useCreateInformationRequestMutation,
    useGetInformationRequestsQuery,
} from '@/store/api/apiSlice';
import type { RequestableField, InformationRequest } from '@/types';
import { parseErrorMessage } from '@/utils/parseErrorMessage';

interface RequestInfoModalProps {
    isOpen: boolean;
    investigationId: string;
    employeeName: string;
    onClose: () => void;
}

// Build the same public URL the backend emails, so we can surface it in-app.
function requestUrl(token: string): string {
    return `${window.location.origin}/background-check/information-request/${token}`;
}

function formatWhen(value?: string | null): string {
    if (!value) return '';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

export default function RequestInfoModal({ isOpen, investigationId, employeeName, onClose }: RequestInfoModalProps) {
    const { data: catalog = [], isLoading: isLoadingCatalog } = useGetRequestableFieldsQuery(undefined, {
        skip: !isOpen,
    });
    const { data: history = [] } = useGetInformationRequestsQuery(investigationId, { skip: !isOpen });
    const [createRequest, { isLoading: isCreating }] = useCreateInformationRequestMutation();

    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [note, setNote] = useState('');
    const [error, setError] = useState('');
    const [created, setCreated] = useState<InformationRequest | null>(null);
    const [copied, setCopied] = useState(false);

    // Group the catalog by section for rendering.
    const grouped = useMemo(() => {
        const map = new Map<string, RequestableField[]>();
        for (const field of catalog) {
            const list = map.get(field.section) ?? [];
            list.push(field);
            map.set(field.section, list);
        }
        return Array.from(map.entries());
    }, [catalog]);

    if (!isOpen) return null;

    const toggle = (key: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const handleSubmit = async () => {
        if (selected.size === 0) {
            setError('Select at least one field to request.');
            return;
        }
        setError('');
        try {
            const result = await createRequest({
                id: investigationId,
                fields: Array.from(selected),
                ...(note.trim() ? { note: note.trim() } : {}),
            }).unwrap();
            setCreated(result);
        } catch (err) {
            setError(parseErrorMessage(err, 'Could not send the request. Please try again.'));
        }
    };

    const handleCopy = async () => {
        if (!created) return;
        try {
            await navigator.clipboard.writeText(requestUrl(created.token));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            /* clipboard unavailable — link is still shown for manual copy */
        }
    };

    const handleClose = () => {
        setSelected(new Set());
        setNote('');
        setError('');
        setCreated(null);
        setCopied(false);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-100 p-5">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-50">
                            <ClipboardList className="h-5 w-5 text-[#FF6B35]" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Request missing information</h2>
                            <p className="text-xs text-gray-500">
                                {employeeName} will get an email with a focused form.
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100"
                    >
                        <X className="h-4 w-4 text-gray-500" />
                    </button>
                </div>

                {/* Success state */}
                {created ? (
                    <div className="flex-1 overflow-y-auto p-6">
                        <div className="flex flex-col items-center text-center">
                            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-50">
                                <CheckCircle2 className="h-7 w-7 text-green-600" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900">Request sent</h3>
                            <p className="mt-1 text-sm text-gray-600">
                                We emailed {created.employeeName} a link to complete {created.fields.length}{' '}
                                {created.fields.length === 1 ? 'field' : 'fields'}.
                            </p>
                        </div>

                        <div className="mt-5 rounded-xl border border-gray-200 bg-gray-50 p-3">
                            <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-500">
                                Shareable link
                            </p>
                            <div className="flex items-center gap-2">
                                <input
                                    readOnly
                                    value={requestUrl(created.token)}
                                    className="flex-1 truncate rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700"
                                />
                                <Button
                                    onClick={handleCopy}
                                    className="flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-2 text-xs font-medium text-white hover:bg-gray-800"
                                >
                                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                                    {copied ? 'Copied' : 'Copy'}
                                </Button>
                            </div>
                        </div>

                        <div className="mt-6 flex justify-end">
                            <Button
                                onClick={handleClose}
                                className="rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-gray-800"
                            >
                                Done
                            </Button>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Body: field picker */}
                        <div className="flex-1 overflow-y-auto p-5">
                            {isLoadingCatalog ? (
                                <div className="flex items-center justify-center py-16">
                                    <Loader2 className="h-8 w-8 animate-spin text-[#FF6B35]" />
                                </div>
                            ) : grouped.length === 0 ? (
                                <p className="py-12 text-center text-sm text-gray-500">
                                    No requestable fields are available.
                                </p>
                            ) : (
                                <div className="space-y-5">
                                    {grouped.map(([section, fields]) => (
                                        <div key={section}>
                                            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                {section}
                                            </h4>
                                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                                {fields.map((field) => {
                                                    const checked = selected.has(field.key);
                                                    return (
                                                        <button
                                                            key={field.key}
                                                            type="button"
                                                            onClick={() => toggle(field.key)}
                                                            className={`flex items-start gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-colors ${
                                                                checked
                                                                    ? 'border-[#FF6B35] bg-[#FF6B35]/5'
                                                                    : 'border-gray-200 hover:border-gray-300'
                                                            }`}
                                                        >
                                                            <span
                                                                className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                                                                    checked
                                                                        ? 'border-[#FF6B35] bg-[#FF6B35] text-white'
                                                                        : 'border-gray-300'
                                                                }`}
                                                            >
                                                                {checked && <Check className="h-3.5 w-3.5" />}
                                                            </span>
                                                            <span className="text-sm text-gray-800">{field.label}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}

                                    {/* Note */}
                                    <div>
                                        <label className="mb-1.5 block text-sm font-medium text-gray-700">
                                            Note to the employee (optional)
                                        </label>
                                        <textarea
                                            value={note}
                                            onChange={(e) => setNote(e.target.value)}
                                            maxLength={2000}
                                            rows={3}
                                            placeholder="e.g. Please add your last 5 years of addresses."
                                            className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:border-[#FF6B35] focus:outline-none"
                                        />
                                    </div>

                                    {/* History */}
                                    {history.length > 0 && (
                                        <div className="border-t border-gray-100 pt-4">
                                            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                Previous requests
                                            </h4>
                                            <div className="space-y-2">
                                                {history.map((req) => (
                                                    <div
                                                        key={req.id}
                                                        className="flex items-center justify-between gap-3 rounded-lg bg-gray-50 px-3 py-2"
                                                    >
                                                        <span className="text-xs text-gray-600">
                                                            {req.fields.length}{' '}
                                                            {req.fields.length === 1 ? 'field' : 'fields'} ·{' '}
                                                            {formatWhen(req.createdAt)}
                                                        </span>
                                                        <span
                                                            className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                                                                req.status === 'completed'
                                                                    ? 'bg-green-50 text-green-700'
                                                                    : 'bg-amber-50 text-amber-700'
                                                            }`}
                                                        >
                                                            {req.status === 'completed' ? 'Completed' : 'Pending'}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {error && (
                                <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between gap-3 border-t border-gray-100 p-5">
                            <span className="text-xs text-gray-500">
                                {selected.size} {selected.size === 1 ? 'field' : 'fields'} selected
                            </span>
                            <div className="flex items-center gap-3">
                                <Button
                                    onClick={handleClose}
                                    className="rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleSubmit}
                                    disabled={selected.size === 0 || isCreating}
                                    className="flex items-center gap-2 rounded-lg bg-[#FF6B35] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#e0581f] disabled:opacity-50"
                                >
                                    {isCreating && <Loader2 className="h-4 w-4 animate-spin" />}
                                    Send request
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
